import React, { useState } from "react";
import PropTypes from "prop-types";
import { useProfile } from "@/_actions/userActions";
import EditProfile from "./forms/EditProfile";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Phone, Mail, Edit, MessageSquare, Loader2 } from "lucide-react";
import CallIcon from "./img/call.svg";
import EditIcon from "./img/edit.svg";
import WhatsAppIcon from "./img/whatsapp.svg";
import { cardTitleClassName, outlineButtonClassName } from "./styles";

export default function UserProfile({ dashboard = false }) {
  const { data: profile = {}, isLoading, mutate: profileMutate } = useProfile();
  const [showEditProfile, toggleEditProfile] = useState(false);
  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ");

  return (
    <div className="@container space-y-4">
      {dashboard ? (
        <h2 className="text-sm font-semibold uppercase text-slate-400">Información de contacto</h2>
      ) : (
        <div className="flex items-start gap-9">
          <h2 className={cardTitleClassName}>Datos de contacto</h2>
          {!isLoading && !showEditProfile ? (
            <Button
              onClick={() => toggleEditProfile(true)}
              type="button"
              variant="outline"
              data-splitbee-event="account-edit-contact"
              className={outlineButtonClassName}
            >
              <img src={EditIcon} alt="" width="24" height="24" />
              Editar
            </Button>
          ) : null}
        </div>
      )}
      {isLoading ? (
        <div className="flex justify-center items-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : profile && !showEditProfile ? (
        dashboard ? (
          <div className="space-y-4">
            <p className="break-words text-xl font-semibold uppercase leading-tight text-gray-900">
              {fullName || "Usuario"}
            </p>
            <div className="flex flex-wrap items-start gap-4">
              <Avatar className="size-16 bg-white @min-[420px]:size-20">
                <AvatarImage
                  src={profile.logo_client || undefined}
                  alt="Logo de la cuenta"
                  className="object-contain"
                />
                <AvatarFallback className="bg-[#6067BE] text-3xl font-semibold text-white">
                  {(fullName || profile.email || "U").trim().charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 space-y-3 text-sm text-gray-700 @min-[420px]:text-base">
                <div className="flex items-center gap-3">
                  <Phone aria-hidden="true" className="size-5 shrink-0" />
                  <span className="break-all">{profile?.contact?.mobile || "—"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MessageSquare aria-hidden="true" className="size-5 shrink-0" />
                  <span className="break-all">{profile?.contact?.whatsapp || "—"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail aria-hidden="true" className="size-5 shrink-0" />
                  <span className="break-all">{profile.email || "—"}</span>
                </div>
              </div>
              <div className="w-full @min-[420px]:w-auto">
                <Button
                  onClick={() => toggleEditProfile(true)}
                  type="button"
                  variant="outline"
                  data-splitbee-event="account-edit-contact"
                >
                  Editar
                  <Edit aria-hidden="true" className="size-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-base text-black">
            {fullName ? <p className="break-words font-bold">{fullName}</p> : null}
            <div className="flex items-center gap-1">
              <img src={CallIcon} alt="Teléfono" width="24" height="24" className="shrink-0" />
              <span className="break-all">{profile?.contact?.mobile || "—"}</span>
            </div>
            <div className="flex items-center gap-1">
              <img src={WhatsAppIcon} alt="WhatsApp" width="24" height="24" className="shrink-0" />
              <span className="break-all">{profile?.contact?.whatsapp || "—"}</span>
            </div>
            <div className="flex items-center gap-1">
              <Mail role="img" aria-label="Email" className="size-6 shrink-0 text-[#1c1b1f]" />
              <span className="break-all">{profile.email || "—"}</span>
            </div>
          </div>
        )
      ) : null}

      {showEditProfile ? (
        <EditProfile
          data={profile}
          callBack={() => {
            toggleEditProfile(false);
            profileMutate();
          }}
          cancel={() => toggleEditProfile(false)}
        />
      ) : null}
    </div>
  );
}

UserProfile.propTypes = {
  dashboard: PropTypes.bool,
};
